/* ============================================================
   Deck preview — sections/deck-preview.liquid
   A pack of cards you can pick up, throw, turn over and arrange.

   One DeckPreview per [data-deck-preview] element. The card list comes
   from the section's JSON manifest, so the Liquid owns which cards exist
   and where the art lives; this file only knows how cards behave.

   Feel, in order of how much it matters:
     - a card moves from wherever you grabbed it, never jumps to centre
     - it lifts (scale, shadow) and tilts with the direction you drag
     - let go and it keeps sliding, bouncing softly off the edges
     - a tap turns it over
   ============================================================ */
(function () {
  "use strict";

  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function rand(a, b) { return a + Math.random() * (b - a); }
  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  /* The ribbon sweep sets per-card transition timing inline; everything
     else must clear it or later tweens run at the wrong speed. */
  function clearTiming(el) {
    el.style.transitionDuration = "";
    el.style.transitionTimingFunction = "";
  }

  function DeckPreview(root) {
    if (root.__deck) root.__deck.destroy();
    root.__deck = this;

    this.root = root;
    this.stage = root.querySelector("[data-stage]");
    this.cardsLayer = root.querySelector("[data-cards]");
    this.dealHint = root.querySelector("[data-deal-hint]");
    /* Starting spread there is no pack to deal from; the hint first
       appears once the pack is squared up (see hintWhenSettled). */
    if (this.dealHint && root.getAttribute("data-start") !== "ribbon") {
      this.dealHint.classList.add("is-shown");
    }

    var title = root.querySelector(".deck-preview__title");
    if (title) {
      this.onTitle = function () {
        root.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
      };
      title.addEventListener("click", this.onTitle);
      this.titleEl = title;
    }
    this.cards = [];
    this.ribbonOrder = null;
    this.zTop = 1000;
    this.faceDown = root.getAttribute("data-face-down") === "true";
    this.flipTimers = [];
    this.shuffleTimers = [];

    var manifest;
    try {
      manifest = JSON.parse(root.querySelector("[data-deck-manifest]").textContent);
    } catch (e) {
      return;
    }
    /* Before build, so the cards are born hidden rather than fading
       out after insertion. is-built lets the loader show — while the
       table is still rolling open the stage stays bare. */
    root.classList.add("is-loading");
    root.classList.add("is-built");
    this.build(manifest);
    this.bindControls();

    /* Hold the reveal until every card face has loaded AND decoded (the
       art runs to ~7 MB; cached loads still pay the decode, which is
       what pops in visibly otherwise); a stall reveals anyway at 12s.
       The loader also holds a minimum dwell so a cached, instant load
       doesn't flash it — the whole entrance stays unhurried. */
    var self = this;
    var t0 = Date.now();
    var imgs = this.stage.querySelectorAll(".deck-preview__face");
    var left = imgs.length;
    function reveal() {
      clearTimeout(self.loadTimer);
      self.loadTimer = setTimeout(function () {
        root.classList.remove("is-loading");
      }, Math.max(0, (reduce ? 0 : 1400) - (Date.now() - t0)));
    }
    function one() { if (--left <= 0) reveal(); }
    this.loadTimer = setTimeout(reveal, 12000);
    for (var j = 0; j < imgs.length; j++) {
      var im = imgs[j];
      if (im.decode) {
        im.decode().then(one, one);
      } else if (im.complete && im.naturalWidth > 0) {
        one();
      } else {
        im.addEventListener("load", one);
        im.addEventListener("error", one);
      }
    }

    if (root.getAttribute("data-start") === "ribbon") {
      requestAnimationFrame(function () { self.ribbon(); });
    }
  }

  var P = DeckPreview.prototype;

  /* ---------- build ---------- */
  P.build = function (manifest) {
    var list = manifest.cards;
    var dx = null;
    /* The manifest reads top of the pack first (the ace of spades faces you
       when you open the box), so it is added to the table back to front:
       the last entry goes down first and the first entry lands on top. */
    for (var k = list.length - 1; k >= 0; k--) {
      var i = this.cards.length;
      var depth = k;

      var el = document.createElement("div");
      el.className = "deck-preview__card";
      if (this.faceDown) el.classList.add("is-flipped");
      el.style.zIndex = String(300 + i);

      var inner = document.createElement("div");
      inner.className = "deck-preview__inner";

      var front = document.createElement("img");
      front.className = "deck-preview__face deck-preview__face--front";
      front.src = list[k].src;
      front.alt = "";
      front.draggable = false;
      front.decoding = "async";
      if (depth < 4) front.setAttribute("fetchpriority", "high");

      var back = document.createElement("img");
      back.className = "deck-preview__face deck-preview__face--back";
      back.src = manifest.back;
      back.alt = "";
      back.draggable = false;
      back.decoding = "async";
      if (this.faceDown && depth < 4) back.setAttribute("fetchpriority", "high");

      var shadowWrap = document.createElement("div");
      shadowWrap.className = "deck-preview__shadow";
      var shadow = document.createElement("div");
      shadow.className = "deck-preview__shadow-card";
      shadowWrap.appendChild(shadow);

      if (this.faceDown) {
        inner.style.transform = "rotateY(180deg)";
        shadow.style.transform = "rotateY(180deg)";
      }

      inner.appendChild(front);
      inner.appendChild(back);
      var liftWrap = document.createElement("div");
      liftWrap.className = "deck-preview__lift";
      liftWrap.appendChild(inner);
      el.appendChild(shadowWrap);
      el.appendChild(liftWrap);
      (this.cardsLayer || this.stage).appendChild(el);
      if (dx === null) dx = -el.offsetWidth * 0.6;

      var st = {
        el: el, inner: inner, shadow: shadow, id: list[k].id,
        turns: this.faceDown ? 1 : 0,
        x: dx + depth * 0.075 + rand(-2.6, 2.6),
        y: depth * 0.085 + rand(-2.6, 2.6),
        base: rand(-0.9, 0.9), rot: 0, s: 1,
        vx: 0, vy: 0, raf: 0, flipped: this.faceDown, lifted: false
      };
      st.rot = st.base;
      this.cards.push(st);
      this.apply(st);
      this.bind(st);
    }
  };

  P.apply = function (st) {
    var sc = st.s * (st.lifted ? 1.07 : 1);
    st.el.style.transform =
      "translate3d(" + st.x.toFixed(2) + "px," + st.y.toFixed(2) + "px,0) rotate(" +
      st.rot.toFixed(2) + "deg) scale(" + sc.toFixed(3) + ")";
  };

  P.dims = function () {
    var el = this.cards[0].el;
    return { w: el.offsetWidth || 180, h: el.offsetHeight || 254 };
  };

  P.bounds = function () {
    var r = this.stage.getBoundingClientRect();
    var d = this.dims();
    return {
      x: Math.max(20, r.width / 2 - d.w * 0.3),
      y: Math.max(20, r.height / 2 - d.h * 0.3)
    };
  };

  P.raise = function (el) { el.style.zIndex = String(++this.zTop); };

  /* The squared pack sits a little left of centre so there is room to
     deal a card beside it, even on a phone. */
  P.homeX = function () { return -this.dims().w * 0.6; };

  /* The rotation accumulates rather than toggling 0↔180, which would
     unwind backwards on the way back. Taps and buttons always turn the
     same way (dir omitted); the deal reveal passes the drag direction. */
  P.turn = function (st, dir) {
    st.flipped = !st.flipped;
    st.turns += dir < 0 ? -1 : 1;
    st.inner.style.transform = "rotateY(" + st.turns * 180 + "deg)";
    st.shadow.style.transform = "rotateY(" + st.turns * 180 + "deg)";
    st.el.classList.toggle("is-flipped", st.flipped);
  };

  /* The deal hint survives taps (flipping the top card is not dealing);
     it goes once a card is dragged or the controls rearrange the table. */
  P.dealt = function () {
    clearTimeout(this.hintTimer);
    if (this.dealHint) this.dealHint.classList.remove("is-shown");
  };

  /* ...and comes back once the pack is squared up in place. */
  P.hintWhenSettled = function (delay) {
    var self = this;
    clearTimeout(this.hintTimer);
    if (!this.dealHint) return;
    this.hintTimer = setTimeout(function () {
      self.dealHint.classList.add("is-shown");
    }, reduce ? 0 : delay);
  };

  /* ---------- drag ---------- */
  P.bind = function (st) {
    var self = this;
    var el = st.el;
    var id = null, px = 0, py = 0, sx = 0, sy = 0, t0 = 0, moved = false;
    var revealed = false;

    function down(e) {
      if (id !== null) return;
      id = e.pointerId;
      el.setPointerCapture(id);
      e.preventDefault();

      cancelAnimationFrame(st.raf);
      el.classList.remove("is-tweening");
      clearTiming(el);
      el.classList.add("is-lifted");
      self.raise(el);
      st.lifted = true;

      px = sx = e.clientX; py = sy = e.clientY;
      t0 = performance.now();
      moved = false;
      revealed = false;
      st.vx = st.vy = 0;
      self.apply(st);
    }

    function move(e) {
      if (e.pointerId !== id) return;
      var dx = e.clientX - px, dy = e.clientY - py;
      px = e.clientX; py = e.clientY;
      if (Math.abs(e.clientX - sx) > 4 || Math.abs(e.clientY - sy) > 4) {
        if (!moved) self.dealt();
        moved = true;
      }

      /* Dealing: a card dragged off the pack turns over as it goes —
         face down to face up and vice versa — rolling in the direction
         of the motion. Once per pickup. */
      if (!revealed && Math.abs(e.clientX - sx) > 28) {
        revealed = true;
        self.turn(st, e.clientX - sx < 0 ? -1 : 1);
      }

      st.x += dx; st.y += dy;
      st.vx = st.vx * 0.55 + dx * 0.45;
      st.vy = st.vy * 0.55 + dy * 0.45;

      var b = self.bounds();
      st.x = clamp(st.x, -b.x, b.x);
      st.y = clamp(st.y, -b.y, b.y);
      st.rot = reduce ? st.base : st.base + clamp(st.vx * 0.55, -11, 11);
      self.apply(st);
    }

    function up(e) {
      if (e.pointerId !== id) return;
      try { el.releasePointerCapture(id); } catch (err) {}
      id = null;
      el.classList.remove("is-lifted");
      st.lifted = false;

      if (!moved && performance.now() - t0 < 320) {
        self.turn(st);
        st.rot = st.base;
        self.apply(st);
        return;
      }
      if (reduce) { st.rot = st.base; self.apply(st); return; }
      self.glide(st);
    }

    el.addEventListener("pointerdown", down);
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
    el.addEventListener("dragstart", function (e) { e.preventDefault(); });
  };

  P.glide = function (st) {
    var self = this;
    var b = this.bounds();
    st.vx *= 0.93; st.vy *= 0.93;
    st.x += st.vx; st.y += st.vy;

    if (st.x < -b.x) { st.x = -b.x; st.vx *= -0.38; }
    if (st.x > b.x)  { st.x = b.x;  st.vx *= -0.38; }
    if (st.y < -b.y) { st.y = -b.y; st.vy *= -0.38; }
    if (st.y > b.y)  { st.y = b.y;  st.vy *= -0.38; }

    st.rot = st.base + clamp(st.vx * 0.55, -11, 11);
    this.apply(st);

    if (Math.abs(st.vx) > 0.12 || Math.abs(st.vy) > 0.12) {
      st.raf = requestAnimationFrame(function () { self.glide(st); });
    } else {
      st.rot = st.base;
      st.el.classList.add("is-tweening");
      this.apply(st);
      setTimeout(function () { st.el.classList.remove("is-tweening"); }, 600);
    }
  };

  /* ---------- arrangements ---------- */
  P.move = function (st, x, y, rot, delay, scale) {
    cancelAnimationFrame(st.raf);
    st.vx = st.vy = 0;
    st.x = x; st.y = y; st.base = rot; st.rot = rot;
    st.s = scale === undefined ? 1 : scale;
    st.el.classList.add("is-tweening");
    clearTiming(st.el);
    st.el.style.transitionDelay = delay + "ms";
    this.apply(st);
    setTimeout(function () {
      st.el.classList.remove("is-tweening");
      st.el.style.transitionDelay = "";
    }, 640 + delay);
  };

  /* bottom to top, by what is actually drawn on top of what */
  P.stacked = function () {
    return this.cards.slice().sort(function (a, b) {
      return Number(a.el.style.zIndex) - Number(b.el.style.zIndex);
    });
  };

  P.restack = function (list) {
    list.forEach(function (st, i) { st.el.style.zIndex = String(300 + i); });
    this.zTop = Math.max(this.zTop, 1000);
  };

  P.clearShuffle = function () {
    this.shuffleTimers.forEach(clearTimeout);
    this.shuffleTimers = [];
  };

  /* Returns the lead time in ms before the pack is settled enough for
     a follow-up arrangement (the shuffle schedules off it). */
  P.squareUp = function () {
    var self = this;
    var hx = this.homeX();
    clearTimeout(this.sweepTimer);
    clearTimeout(this.gatherTimer);
    this.clearShuffle();
    var fromSpread = !!this.ribbonOrder;
    this.ribbonOrder = null;
    var list = this.stacked();
    this.restack(list);

    function place() {
      list.forEach(function (st, i) {
        var d = list.length - 1 - i;
        /* Out of a gathered packet the pack grows and moves as one
           piece; only a loose table gets the card-by-card ripple. */
        var delay = reduce || fromSpread ? 0 : i * 7;
        self.move(st, hx + d * 0.075 + rand(-2.6, 2.6), d * 0.085 + rand(-2.6, 2.6), rand(-0.9, 0.9), delay, 1);
      });
    }

    if (!fromSpread || reduce) {
      place();
      this.hintWhenSettled(1000);
      return 900;
    }

    /* From a spread the cards first collect into a packet at their
       small size, then the packet grows and slides home. Both phases
       run with no per-card delay, and the grow waits for the gather
       tween to fully settle, so the deck moves as one unit throughout. */
    var sc = list[0].s || 1;
    list.forEach(function (st) {
      self.move(st, rand(-2.2, 2.2), rand(-2.2, 2.2), rand(-1.6, 1.6), 0, sc);
    });
    this.gatherTimer = setTimeout(place, 640);
    this.hintWhenSettled(1700);
    return 1600;
  };

  /* The spread's geometry: cards size themselves to the stage so that
     roughly a tenth of each shows once the whole pack is laid out. */
  P.ribbonGeom = function () {
    var r = this.stage.getBoundingClientRect();
    var d = this.dims();
    var n = this.cards.length;
    var want = (r.width - 24) / (1 + 0.11 * (n - 1));
    var sc = clamp(want / d.w, 0.24, 0.8);
    return {
      sc: sc,
      wide: Math.max(40, r.width - d.w * sc - 24),
      step: n > 1 ? Math.max(40, r.width - d.w * sc - 24) / (n - 1) : 0
    };
  };

  /* Quiet re-lay of an existing spread (resize): cards go straight to
     their slots, no gather-and-sweep performance. */
  P.layRibbon = function () {
    var self = this;
    clearTimeout(this.sweepTimer);
    var list = this.stacked();
    var g = this.ribbonGeom();
    list.forEach(function (st, i) {
      self.move(st, -g.wide / 2 + i * g.step, 0, rand(-1.6, 1.6), 0, g.sc);
    });
    this.ribbonOrder = list;
  };

  /* One long line, laid like a real spread: the pack first slides to the
     left edge as one block, then sweeps across the stage leaving cards
     behind, bottom card first. */
  P.ribbon = function () {
    var self = this;
    var list = this.stacked();
    var n = list.length;
    var g = this.ribbonGeom();
    var sc = g.sc, wide = g.wide, step = g.step;

    clearTimeout(this.sweepTimer);
    clearTimeout(this.gatherTimer);
    clearTimeout(this.hintTimer);
    this.clearShuffle();
    this.restack(list);
    this.ribbonOrder = list;

    if (reduce) {
      list.forEach(function (st, i) {
        self.move(st, -wide / 2 + i * step, 0, rand(-1.6, 1.6), 0, sc);
      });
      return;
    }

    /* Every card travels at the same speed and stops at its own slot, so
       the pack crosses the stage as one block shedding cards behind it. */
    var SWEEP_MS = 900;
    function sweep() {
      list.forEach(function (st, i) {
        cancelAnimationFrame(st.raf);
        st.vx = st.vy = 0;
        st.x = -wide / 2 + i * step;
        st.y = 0;
        st.base = st.rot = rand(-1.6, 1.6);
        st.s = sc;
        st.el.classList.add("is-tweening");
        st.el.style.transitionDelay = "";
        st.el.style.transitionDuration = Math.max(140, SWEEP_MS * (n > 1 ? i / (n - 1) : 1)) + "ms";
        st.el.style.transitionTimingFunction = "cubic-bezier(0.2, 0.2, 0.55, 1)";
        self.apply(st);
      });
      self.sweepTimer = setTimeout(function () {
        list.forEach(function (st) {
          st.el.classList.remove("is-tweening");
          clearTiming(st.el);
        });
      }, SWEEP_MS + 700);
    }

    list.forEach(function (st) {
      self.move(st, -wide / 2 + rand(-2.2, 2.2), rand(-2.2, 2.2), rand(-0.9, 0.9), 0, sc);
    });
    this.sweepTimer = setTimeout(sweep, 620);
  };

  /* The pack as it leaves the box. The cards array is already in that
     order. Cards re-sort within the current arrangement: a spread
     re-lays itself in order and stays a spread; anything else forms
     the pack in order at home. */
  P.newDeckOrder = function () {
    var self = this;
    var faceDown = this.faceDown;
    var n = this.cards.length;
    this.clearFlipWave();
    this.clearShuffle();
    clearTimeout(this.sweepTimer);
    clearTimeout(this.gatherTimer);

    if (this.ribbonOrder) {
      /* A sorting wave, left to right: each card that is out of place
         takes its turn — rises above the table, glides to its slot,
         turning over mid-flight if it is the wrong way up, and settles
         into the pile order as it lands. Cards already sitting right
         are left alone, so an ordered spread stays perfectly still. */
      var g = this.ribbonGeom();
      var movers = 0;
      this.cards.forEach(function (st, i) {
        var tx = -g.wide / 2 + i * g.step;
        var inPlace = Number(st.el.style.zIndex) === 300 + i &&
          Math.abs(st.x - tx) < 1 && Math.abs(st.y) < 1 &&
          Math.abs(st.s - g.sc) < 0.01 && st.flipped === faceDown;
        if (inPlace) return;
        self.shuffleTimers.push(setTimeout(function () {
          if (reduce) {
            st.el.style.zIndex = String(300 + i);
          } else {
            st.el.style.zIndex = String(++self.zTop);
            self.shuffleTimers.push(setTimeout(function () {
              st.el.style.zIndex = String(300 + i);
            }, 600));
          }
          if (st.flipped !== faceDown) self.turn(st);
          self.move(st, tx, 0, rand(-1.6, 1.6), 0, g.sc);
        }, reduce ? 0 : movers * 60));
        movers++;
      });
      this.ribbonOrder = this.cards.slice();
      return;
    }

    this.cards.forEach(function (st) {
      if (st.flipped !== faceDown) self.turn(st);
    });
    this.restack(this.cards);
    var hx = this.homeX();
    this.cards.forEach(function (st, i) {
      var d = n - 1 - i;
      self.move(st, hx + d * 0.075 + rand(-2.6, 2.6), d * 0.085 + rand(-2.6, 2.6), rand(-0.9, 0.9), reduce ? 0 : i * 7, 1);
    });
    this.hintWhenSettled(1000);
  };

  P.clearFlipWave = function () {
    this.flipTimers.forEach(clearTimeout);
    this.flipTimers = [];
  };

  /* Every card turns over where it lies; face-up ones go down and vice
     versa, so a mixed table stays mixed, just the other way round.
     Spread out, the turns cascade from the bottom of the pack up — each
     flip runs at full speed, only the starts are spread over about a
     second. Anywhere else the pack turns as one block. */
  P.flipAll = function () {
    var self = this;
    this.clearFlipWave();
    var list = this.stacked();
    if (!this.ribbonOrder || reduce) {
      list.forEach(function (st) { self.turn(st); });
      return;
    }
    var step = list.length > 1 ? 1000 / (list.length - 1) : 0;
    list.forEach(function (st, i) {
      self.flipTimers.push(setTimeout(function () {
        self.turn(st);
      }, Math.round(i * step)));
    });
  };

  /* The shuffle: the pack cuts into two half-deck packs side by side,
     then the cards are thrown back together onto one pile in a
     genuinely random order — each throw lands on top of the last, so
     the pile IS the shuffle — and the pack squares back up. Quick and
     simple; faces stay however they were. */
  P.shuffle = function () {
    var self = this;
    var n = this.cards.length;
    var d = this.dims();
    var hx = this.homeX();

    var lead = this.squareUp(); /* beat 1 — collect (clears older runs) */
    clearTimeout(this.hintTimer);
    var timers = this.shuffleTimers;

    var P0 = this.stacked();
    var c = Math.ceil(n / 2);
    var A = P0.slice(0, c), B = P0.slice(c);

    /* A true random order (Fisher–Yates); the throws below build
       exactly this stack, bottom card first. */
    var order = P0.slice();
    for (var i = order.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t0 = order[i]; order[i] = order[j]; order[j] = t0;
    }

    if (reduce) {
      this.restack(order);
      this.squareUp();
      return;
    }

    var split = d.w * 0.62;

    timers.push(setTimeout(function () { /* beat 2 — cut into two
        packs side by side */
      [{ half: A, x: -split }, { half: B, x: split }].forEach(function (side) {
        side.half.forEach(function (st, j2) {
          var o = side.half.length - 1 - j2;
          self.move(st, side.x + o * 0.075 + rand(-2.2, 2.2), o * 0.085 + rand(-2.2, 2.2), rand(-1.4, 1.4), 0, 1);
        });
      });
    }, lead));

    /* beat 3 — thrown together: cards fly onto one pile in shuffled
       order, each landing on top of the last. */
    var step = 700 / n;
    order.forEach(function (st, i3) {
      timers.push(setTimeout(function () {
        st.el.style.zIndex = String(++self.zTop);
        self.move(st, hx + rand(-5, 5), rand(-5, 5), rand(-6, 6), 0, 1);
        st.el.style.transitionDuration = rand(0.28, 0.4).toFixed(2) + "s";
        st.el.style.transitionTimingFunction = "cubic-bezier(0.32, 0.72, 0.24, 1)";
      }, Math.round(lead + 750 + i3 * step + rand(0, step))));
    });

    timers.push(setTimeout(function () { /* beat 4 — square back up */
      self.squareUp();
    }, lead + 1950));
  };

  /* ---------- share picture ---------- */
  /* Redraws the table as a 1080x1920 story image — the header logo on
     top, the cards exactly as they lie — then hands it to the system
     share sheet (mobile) or downloads it (desktop). */
  P.sharePicture = function () {
    var self = this;
    if (this.sharing) return;
    this.sharing = true;
    var btn = this.root.querySelector('[data-action="share"]');
    if (btn) btn.disabled = true;
    var done = function () {
      self.sharing = false;
      if (btn) btn.disabled = false;
    };

    var cache = {};
    function load(src) {
      if (!src) return Promise.resolve(null);
      if (!cache[src]) {
        cache[src] = new Promise(function (res) {
          var im = new Image();
          im.crossOrigin = "anonymous"; /* Shopify CDN sends ACAO:* */
          im.onload = function () { res(im); };
          im.onerror = function () { res(null); };
          im.src = src;
        });
      }
      return cache[src];
    }

    var logoEl = document.querySelector(".archive-header__logo");
    var order = this.stacked();
    var faces = order.map(function (st) {
      var side = st.flipped ? ".deck-preview__face--back" : ".deck-preview__face--front";
      return st.el.querySelector(side).src;
    });

    Promise.all([load(logoEl && logoEl.src)].concat(faces.map(load))).then(function (imgs) {
      var logo = imgs[0];
      var W = 1080, H = 1920;
      var c = document.createElement("canvas");
      c.width = W; c.height = H;
      var ctx = c.getContext("2d");

      ctx.fillStyle = getComputedStyle(self.stage).backgroundColor || "#f5f5f5";
      ctx.fillRect(0, 0, W, H);

      var logoW = 0, logoH = 0;
      if (logo) {
        logoW = Math.min(270, W * 0.27); /* 10% under the old 300 */
        logoH = logoW * logo.height / logo.width;
      }

      /* A spread is far wider than tall: it turns 90° and runs edge to
         edge down the tall axis, ace end up top, the logo overlaid.
         Anything else crops to the cards themselves — a lone pack
         fills the frame, scattered cards frame their own bounds. */
      var stageR = self.stage.getBoundingClientRect();
      var turned = !!self.ribbonOrder;
      var d = self.dims();
      var scale, ox, oy;
      if (turned) {
        /* The cards span the stage width minus its 12px edge insets;
           a whisker of breathing room at either end. */
        scale = (H - 56) / (stageR.width - 24);
        ox = W / 2;
        oy = H / 2;
      } else {
        var minX = 1e9, maxX = -1e9, minY = 1e9, maxY = -1e9;
        order.forEach(function (st) {
          var a = Math.abs(Math.cos(st.rot * Math.PI / 180));
          var b = Math.abs(Math.sin(st.rot * Math.PI / 180));
          var hw = (d.w * a + d.h * b) * st.s / 2;
          var hh = (d.w * b + d.h * a) * st.s / 2;
          if (st.x - hw < minX) minX = st.x - hw;
          if (st.x + hw > maxX) maxX = st.x + hw;
          if (st.y - hh < minY) minY = st.y - hh;
          if (st.y + hh > maxY) maxY = st.y + hh;
        });
        var margin = 90;
        scale = Math.min((W - margin * 2) / (maxX - minX), (H - margin * 2) / (maxY - minY));
        ox = W / 2 - (minX + maxX) / 2 * scale;
        oy = H / 2 - (minY + maxY) / 2 * scale;
      }

      ctx.save();
      ctx.translate(ox, oy);
      if (turned) ctx.rotate(-Math.PI / 2);

      order.forEach(function (st, i) {
        var im = imgs[i + 1];
        if (!im) return;
        var cw = d.w * st.s * scale, ch = d.h * st.s * scale;
        ctx.save();
        ctx.translate(st.x * scale, st.y * scale);
        ctx.rotate(st.rot * Math.PI / 180);
        /* Constant and subtle: scaling with the export zoom stacked up
           heavy smears across a dense spread (56 overlapping shadows). */
        ctx.shadowColor = "rgba(11, 11, 11, 0.05)";
        ctx.shadowBlur = 6;
        ctx.shadowOffsetY = 3;
        ctx.drawImage(im, -cw / 2, -ch / 2, cw, ch);
        ctx.restore();
      });
      ctx.restore();

      /* The logo goes on last, over the cards — middle height, left,
         with a little margin off the edge. */
      if (logo) ctx.drawImage(logo, 48, (H - logoH) / 2, logoW, logoH);

      c.toBlob(function (blob) {
        done();
        if (!blob) return;
        var file = new File([blob], "Anyone Playing Cards.png", { type: "image/png" });
        var fallback = function () {
          var a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = "Anyone Playing Cards.png";
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(function () { URL.revokeObjectURL(a.href); }, 4000);
        };
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          navigator.share({ files: [file] }).catch(function (e) {
            if (e && e.name !== "AbortError") fallback();
          });
        } else {
          fallback();
        }
      }, "image/png");
    }).catch(done);
  };

  P.bindControls = function () {
    var self = this;
    var map = {
      square: function () { self.squareUp(); },
      ribbon: function () { self.ribbon(); },
      flip: function () { self.flipAll(); },
      order: function () { self.newDeckOrder(); },
      shuffle: function () { self.shuffle(); }
    };
    this.onClick = function (e) {
      var btn = e.target.closest("[data-action]");
      if (!btn || !self.root.contains(btn)) return;
      var action = btn.getAttribute("data-action");
      /* Sharing photographs the table; it must not disturb it. */
      if (action === "share") { self.sharePicture(); return; }
      var fn = map[action];
      /* dealt() first: it hides the hint and cancels a pending show;
         the action then schedules its own if the pack ends in place. */
      if (fn) { self.dealt(); fn(); }
    };
    this.root.addEventListener("click", this.onClick);

    var rt;
    var lastR = this.stage.getBoundingClientRect();
    this.onResize = function () {
      clearTimeout(rt);
      rt = setTimeout(function () {
        /* Phones fire resize as the URL bar collapses during scroll;
           the stage (svh-sized) hasn't actually changed then, and
           nothing should move. */
        var r = self.stage.getBoundingClientRect();
        if (Math.abs(r.width - lastR.width) < 1 && Math.abs(r.height - lastR.height) < 1) return;
        lastR = r;
        if (self.ribbonOrder) { self.layRibbon(); return; }
        var b = self.bounds();
        self.cards.forEach(function (st) {
          st.x = clamp(st.x, -b.x, b.x);
          st.y = clamp(st.y, -b.y, b.y);
          self.apply(st);
        });
      }, 140);
    };
    window.addEventListener("resize", this.onResize);

    /* Android's long-press image menu (iOS is handled by
       -webkit-touch-callout in the CSS). */
    this.onContext = function (e) {
      if (e.target.closest(".deck-preview__card")) e.preventDefault();
    };
    this.stage.addEventListener("contextmenu", this.onContext);
  };

  P.destroy = function () {
    if (this.titleEl) this.titleEl.removeEventListener("click", this.onTitle);
    if (this.onContext) this.stage.removeEventListener("contextmenu", this.onContext);
    this.clearFlipWave();
    this.clearShuffle();
    clearTimeout(this.sweepTimer);
    clearTimeout(this.gatherTimer);
    clearTimeout(this.hintTimer);
    clearTimeout(this.loadTimer);
    this.root.classList.remove("is-loading");
    this.root.classList.remove("is-built");
    window.removeEventListener("resize", this.onResize);
    this.root.removeEventListener("click", this.onClick);
    this.cards.forEach(function (st) {
      cancelAnimationFrame(st.raf);
      if (st.el.parentNode) st.el.parentNode.removeChild(st.el);
    });
    this.cards = [];
    this.root.__deck = null;
  };

  /* ---------- boot ---------- */
  /* On desktop the opener sits just below the gallery, on its left
     edge. The gallery's position depends on the layout, so it is
     measured live and kept in sync; phones lay out via the
     stylesheet. */
  function alignOpener(root) {
    var pill = root.querySelector(".deck-preview__title");
    if (!pill) return;
    function place() {
      if (!root.isConnected) return;
      pill.style.marginLeft = "";
      root.style.marginTop = "";
      var media = document.querySelector(".pdp__media");
      if (!media || !window.matchMedia("(min-width: 750px)").matches) return;
      var m = media.getBoundingClientRect();
      var r = root.getBoundingClientRect();
      var p = pill.getBoundingClientRect();
      pill.style.marginLeft = Math.round(m.left - r.left) + "px";
      root.style.marginTop = Math.round(m.bottom + 16 - p.top) + "px";
    }
    place();
    setTimeout(place, 1500); /* late layout shifts (images, fonts) */
    window.addEventListener("load", place, { once: true });
    window.addEventListener("resize", function () { place(); });
  }

  /* The simulator ships collapsed: just its title. The first click
     (or Enter/Space) on the title rolls the table open, and only then
     does the deck build — the ~7 MB of art downloads on demand. */
  function boot(root) {
    alignOpener(root);
    var title = root.querySelector(".deck-preview__title");
    if (!title || !root.classList.contains("is-collapsed")) {
      root.classList.remove("is-collapsed");
      new DeckPreview(root);
      return;
    }
    function open(e) {
      if (e.type === "keydown" && e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      title.removeEventListener("click", open);
      title.removeEventListener("keydown", open);
      title.setAttribute("aria-expanded", "true");
      /* One thing at a time: the bare table rolls open first; the
         deck starts building (and the loader appears) once it has
         settled. The page rides along to meet the new content. */
      root.classList.remove("is-collapsed");
      setTimeout(function () {
        root.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
      }, reduce ? 0 : 250);
      setTimeout(function () {
        if (root.isConnected) new DeckPreview(root);
      }, reduce ? 0 : 850);
    }
    title.addEventListener("click", open);
    title.addEventListener("keydown", open);
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll("[data-deck-preview]");
    for (var i = 0; i < roots.length; i++) boot(roots[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { init(); });
  } else {
    init();
  }

  /* theme editor: sections re-render when their settings change */
  document.addEventListener("shopify:section:load", function (e) { init(e.target); });
  document.addEventListener("shopify:section:unload", function (e) {
    var root = e.target.querySelector("[data-deck-preview]");
    if (!root) return;
    if (root.__deck) root.__deck.destroy();
  });

  window.DeckPreview = DeckPreview;
})();
